// Angular
import { Component, EventEmitter, Input, Output } from '@angular/core';
// Model
import { EngagementFileChange } from './model/engagement-file-upload.model';

@Component({
	selector: 'cv-engagement-file-upload',
	templateUrl: './engagement-file-upload.component.html',
	styleUrls: ['./engagement-file-upload.component.scss']
})
export class EngagementFileUploadComponent {
	@Input() errorMessage: string | null = null;
	@Output() fileSelected = new EventEmitter<EngagementFileChange>();
	@Output() fileRemoved = new EventEmitter<void>();
	public file: File | null = null;

	/**
	 * Handles the file input change event.
	 * Emits the selected file and its validity to the parent component.
	 * If the file is invalid (non-PDF), sets an error message and clears the input.
	 *
	 * @param event - The file input change event triggered by the user.
	 */
	public onFileInput(event: Event): void {
		const input = event.target as HTMLInputElement;
		const file = input?.files?.[0];
		// Reset previous error
		this.errorMessage = null;

		if (!file) return;
		this.file = file;

		if (file.type === 'application/pdf') {
			this.fileSelected.emit({ file: file, isValid: true });
		} else {
			this.errorMessage = 'Only PDF files can be uploaded. Please try again.';
			this.fileSelected.emit({ file: null, isValid: false });
			// Reset the input so same invalid file can trigger change again
			input.value = '';
		}
	}

	/**
	 * Clears the selected file and associated error state.
	 * Also resets the native file input element so the same file can be re-selected.
	 * Emits a fileRemoved event to notify the parent component.
	 *
	 * @param fileInputElement - A reference to the native file input element to clear its value.
	 */
	public removeFile(fileInputElement): void {
		this.file = null;
		this.errorMessage = null;
		this.fileRemoved.emit();

		// Clear the actual <input type="file"> value
		if (fileInputElement) {
			fileInputElement.value = '';
		}
	}

	/**
	 * Converts the raw file size in bytes into a human-readable string.
	 *
	 * @param size - The size of the file in bytes.
	 * @returns A formatted string representing the file size in KB or MB.
	 */
	public getFileSize(size: number): string {
		const kb = size / 1024;
		return kb > 1024
			? `${(kb / 1024).toFixed(2)} MB`
			: `${kb.toFixed(2)} KB`;
	}
}